import { ErrorMessage } from '@hookform/error-message';
import { type FC, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-dropdown-select';
import { Controller, useForm } from 'react-hook-form';
import type { Option } from 'react-multi-select-component';
import { MessagingTypes } from '../../../modules/enums/types';
import { useAuth, useMutationRequest, useNotifications } from '../../../modules/hooks';
import { endpoints } from '../../../modules/mappers/urls';
import { getApiErrorMessage } from '../../../modules/utils/transform';

interface MessagePreviewModalProps {
  show: boolean;
  handleClose: () => void;
  handleSave?: () => void;

  templateId: string;
  reservationId: string;
}

interface SendNowFormType {
  channel?: string;
  forceSend?: boolean;
  channelOptions?: Option[];
}

const channelOptions: Option[] = [
  { value: 'whatsapp', label: 'Whatsapp' },
  { value: 'sms', label: 'SMS' },
  { value: 'auto', label: 'Auto' },
];

export const SendNowModal: FC<MessagePreviewModalProps> = ({
  show,
  handleClose,
  templateId,
  reservationId,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const [selectedChannel, setSelectedChannel] = useState<Option[]>([
    {
      value: 'auto',
      label: 'Auto',
    },
  ]);
  const { credentialsInfo } = useAuth()!;

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,

    formState: { errors },
  } = useForm<SendNowFormType>({
    mode: 'onChange',
    defaultValues: {
      channelOptions: [{ label: '', value: '' }],
      forceSend: false,
    },
  });

  const [
    { data: sendNowResult, loading: loadingSendNow, error: errorSendNow },
    sendNowMessageTemplate,
  ] = useMutationRequest(endpoints.SEND_MESSAGING_TEMPLATE, 'post', {
    authToken: credentialsInfo?.token,
  });

  const forceSendValue = watch('forceSend');

  const onSubmit = async (data: SendNowFormType) => {
    const channel =
      data?.channelOptions && data?.channelOptions?.[0]?.value !== MessagingTypes.AUTO
        ? data?.channelOptions?.[0]?.value
        : undefined;

    if (templateId && reservationId) {
      const options = channel
        ? {
            channel,
            forceSend: data?.forceSend || false,
          }
        : {
            forceSend: data?.forceSend || false,
          };

      const requestBody = { templateId, reservationId, options };

      sendNowMessageTemplate(requestBody);
    }
  };

  const onSkipAutomaticMessage = (editorState: boolean) => {
    setValue('forceSend', editorState);
  };

  useEffect(() => {
    register('forceSend');
  }, [register]);

  useEffect(() => {
    if (sendNowResult) {
      reset({ channelOptions: [{ label: '', value: '' }], forceSend: false });
      setSelectedChannel([
        {
          value: 'auto',
          label: 'Auto',
        },
      ]);
      handleClose();
    }

    if (errorSendNow) {
      const message = getApiErrorMessage(errorSendNow);

      if (message.toLowerCase().includes('message already sent')) {
        setSimpleToasts({
          type: 'warning',
          message:
            'The message has already been sent. If you want to send it again, please enable the "Force Send" option.',
          show: true,
        });
      } else {
        setSimpleToasts({ type: 'danger', message, show: true });
      }
    }
  }, [sendNowResult, errorSendNow]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="post-editor-modal modal-200w"
      backdrop="static"
      centered
    >
      <Modal.Header>
        <Modal.Title className="editor-title">Send now</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Row className="mt-8px">
            <Form.Group className="border-bottom">
              <Form.Label className="editor-title-label">Channel</Form.Label>
              <Controller
                name="channelOptions"
                control={control}
                rules={{ required: 'This is required.' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={channelOptions}
                    values={selectedChannel}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="channel"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Form.Group>
          </Row>

          <Row>
            <Col className="mt-24px">
              <Form.Check
                type="checkbox"
                defaultChecked={forceSendValue}
                onChange={(e) => onSkipAutomaticMessage(e.currentTarget.checked)}
                label="Force Send"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button className="cancel-button" onClick={handleClose}>
            Close
          </Button>
          <Button className="save-button" type="submit" disabled={loadingSendNow}>
            {loadingSendNow ? 'Loading...' : 'Send'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
